import { Text, SkeletonInput, InfoTooltip, CheckboxField } from "@wayflyer/flyui";
import { IconArrowDown16Line, IconArrowUp16Line } from "@wayflyer/flyui-icons/16/line";
import { useAnalyticsContext } from "shared-utils";
import { analytics } from "shared-ui";

import { MetricReferenceDirection } from "../shared";
import { TooltipContainer } from "../styled";

import {
  CheckboxContainer,
  MetricData,
  MetricReferenceBasicValueText,
  MetricReferenceText,
  MetricReferenceValueText,
  MetricSummaryItemWrapper,
  Space,
  SummaryContainer,
  SummaryItemContainer,
  SummaryItemTextStyled,
  SummaryItemWrapper,
  SummaryValueRow,
} from "./styled";

export type SummaryPanelType = "SUCCESS_DIRECTION" | "BASIC";

export interface SummaryPanelProps {
  summaryItems: SummaryPanelItemProps[];
  loading?: boolean;
  type?: SummaryPanelType;
  metricReferenceText?: string;
  onSelect: (metricType: string, selected: boolean) => void;
  useAnalytics?: boolean;
}

export interface SummaryPanelItemProps {
  metricType: string;
  metricReferenceType?: string;
  title: string;
  metricValue?: string;
  metricReferenceValue?: string;
  metricReferenceDirection?: MetricReferenceDirection;
  metricReferenceSuccess?: boolean;
  metricReferenceText?: string;
  tooltip?: string;
  loading?: boolean;
  type?: SummaryPanelType;
  selected?: boolean;
  extraTitleHeight?: boolean;
  onSelect?: (metricType: string, selected: boolean) => void;
  useAnalytics?: boolean;
  hasCheckbox?: boolean;
}

const MAX_ITEMS_STD_TITLE = 6;

/**
 * SummaryPanel component
 */
export const SummaryPanel = ({
  summaryItems,
  loading = false,
  metricReferenceText = "",
  type,
  onSelect,
  useAnalytics = true,
}: SummaryPanelProps): UIElement => {
  const extraTitleHeight = summaryItems.length > MAX_ITEMS_STD_TITLE;
  // Keyed by metric type, not position, so an item that arrives later does not re-create the ones after it.
  const seenMetricTypes = new Map<string, number>();
  return (
    <SummaryContainer>
      {summaryItems.map((summaryItem) => {
        const occurrence = seenMetricTypes.get(summaryItem.metricType) ?? 0;
        seenMetricTypes.set(summaryItem.metricType, occurrence + 1);
        return (
          <SummaryPanelItem
            type={type}
            useAnalytics={useAnalytics}
            metricReferenceText={metricReferenceText}
            {...summaryItem}
            key={`${summaryItem.metricType}_${occurrence}`}
            loading={loading}
            onSelect={onSelect}
            extraTitleHeight={extraTitleHeight}
          />
        );
      })}
    </SummaryContainer>
  );
};

/**
 * SummaryPanel component
 */
const SummaryPanelItem = ({
  title,
  metricType,
  metricValue = "",
  metricReferenceValue = "",
  metricReferenceDirection = MetricReferenceDirection.SAME,
  metricReferenceSuccess = true,
  loading = false,
  tooltip = "",
  metricReferenceText = "",
  type = "SUCCESS_DIRECTION",
  selected = true,
  extraTitleHeight = false,
  onSelect,
  useAnalytics,
  hasCheckbox = true,
}: SummaryPanelItemProps) => {
  const showRefText = metricReferenceValue && metricReferenceText;

  const analyticsContext = useAnalyticsContext();

  const onChange = (checked: boolean) => {
    if (useAnalytics) {
      analytics.fireClickEvent(analyticsContext, { type: "checkbox", buttonValue: metricType });
    }

    if (onSelect) {
      onSelect(metricType, checked);
    }
  };

  return (
    <SummaryItemContainer data-testid={`summary-${title}`}>
      <SummaryItemWrapper>
        <MetricSummaryItemWrapper>
          <SummaryItemTextStyled extraTitleHeight={extraTitleHeight}>
            {hasCheckbox ? (
              <CheckboxContainer>
                <CheckboxField
                  key={metricType}
                  size="small"
                  name={`${metricType}-field`}
                  options={{ name: metricType, label: title, defaultChecked: selected, onChange }}
                />
              </CheckboxContainer>
            ) : (
              <div>{title}</div>
            )}
            {tooltip ? (
              <TooltipContainer>
                <InfoTooltip content={tooltip} iconSize="regular" />
              </TooltipContainer>
            ) : null}
          </SummaryItemTextStyled>
          <MetricData>
            {loading ? (
              <Space>
                <SkeletonInput small={false} />
                <SkeletonInput small={true} />
              </Space>
            ) : (
              <>
                <SummaryValueRow>
                  <Text fontWeight="semiBold">{metricValue}</Text>
                </SummaryValueRow>
                {type === "SUCCESS_DIRECTION" ? (
                  <MetricReferenceValueText success={metricReferenceSuccess}>
                    {metricReferenceDirection === MetricReferenceDirection.UP ? (
                      <IconArrowUp16Line />
                    ) : metricReferenceDirection === MetricReferenceDirection.DOWN ? (
                      <IconArrowDown16Line />
                    ) : null}
                    {metricReferenceValue}
                    {showRefText ? <MetricReferenceText>{metricReferenceText}</MetricReferenceText> : null}
                  </MetricReferenceValueText>
                ) : (
                  <MetricReferenceBasicValueText>
                    {metricReferenceValue}
                    {showRefText ? <MetricReferenceText>{metricReferenceText}</MetricReferenceText> : null}
                  </MetricReferenceBasicValueText>
                )}
              </>
            )}
          </MetricData>
        </MetricSummaryItemWrapper>
      </SummaryItemWrapper>
    </SummaryItemContainer>
  );
};
